import {
  shutdown,
  showNewMessage,
  showSpace,
} from "@intercom/messenger-js-sdk";
import { useSyncExternalStore } from "react";

/** Mailbox every support entry point falls back to. */
export const SUPPORT_EMAIL = "support@shaarp.ai";

/** Intercom space listing the conversations the user already opened. */
const TICKETS_SPACE = "tickets";

/**
 * How many times the messenger has been booted on this page — 0 until the
 * first boot, and back to 0 once it is shut down.
 *
 * Doubles as the readiness flag, because the messenger only boots inside
 * `UserProvider` and several blocking screens (a fatal team-loading error, for
 * one) render *above* that provider — so a support link on those screens would
 * silently do nothing. Callers check readiness before offering the chat, and
 * fall back to the mailbox otherwise.
 *
 * A count rather than a flag because the messenger re-boots in place whenever
 * its identity changes (switching teams re-identifies it), and a re-boot resets
 * the launcher to the position the boot carried — so the placement driver has
 * to know a new one happened, not just that one did at some point.
 */
let bootCount = 0;
const bootListeners = new Set<() => void>();

function subscribeMessengerBoot(listener: () => void): () => void {
  bootListeners.add(listener);
  return () => {
    bootListeners.delete(listener);
  };
}

function getMessengerBootSnapshot(): number {
  return bootCount;
}

function getMessengerReadySnapshot(): boolean {
  return bootCount > 0;
}

/**
 * Called right after every `Intercom()` boot, so the chat entry points arm and
 * the launcher's placement is re-applied on top of what the boot reset.
 */
export function markSupportMessengerBooted(): void {
  bootCount += 1;
  bootListeners.forEach((listener) => listener());
}

/**
 * Tears the messenger down, taking the previous user's conversation with it.
 *
 * Called when the provider that booted it unmounts — signing out, in practice.
 * Leaving it running would keep a messenger identified as the user who just
 * left floating over the sign-in screen, and leave the in-app entry points
 * offering a chat that belongs to nobody on screen.
 */
export function shutdownSupportMessenger(): void {
  if (bootCount === 0) return;
  bootCount = 0;
  shutdown();
  bootListeners.forEach((listener) => listener());
}

/** Subscribes a component to whether the messenger is booted at all. */
export function useSupportMessengerReady(): boolean {
  return useSyncExternalStore(
    subscribeMessengerBoot,
    getMessengerReadySnapshot,
  );
}

/**
 * Subscribes a component to the messenger's boot count — 0 before the first
 * boot, and one more on every re-boot. What anything that has to re-apply
 * something the boot reset should depend on.
 */
export function useSupportMessengerBoot(): number {
  return useSyncExternalStore(subscribeMessengerBoot, getMessengerBootSnapshot);
}

/**
 * Opens the messenger straight on a new conversation, optionally pre-filled.
 * Does nothing when the messenger isn't booted — callers gate on
 * {@link useSupportMessengerReady} before offering the chat.
 *
 * @param prefill Text dropped into the composer — the user still sends it.
 */
export function openSupportConversation(prefill: string = ""): void {
  if (bootCount === 0) return;
  showNewMessage(prefill);
}

/** Opens the messenger on the user's existing tickets. */
export function openSupportTickets(): void {
  if (bootCount === 0) return;
  showSpace(TICKETS_SPACE);
}

/** Hands the support request over to the user's mail client. */
export function openSupportEmail(): void {
  window.open(`mailto:${SUPPORT_EMAIL}`, "_blank");
}
